/** Oracle Alert Center — full alert history with acknowledge workflow. */
import React, { useEffect, useState } from "react";
import { ShieldAlert, Check, X, Archive as ArchiveIcon, Eye } from "lucide-react";
import { api } from "./commandApi";

export const SEVERITY_STYLE = {
  critical: { color: "#D92D20", label: "CRITICAL" },
  priority: { color: "#E0A526", label: "PRIORITY" },
  notice: { color: "#9FA8D4", label: "NOTICE" },
  info: { color: "#7B7168", label: "INFO" },
};

const FILTERS = [["new", "NEW"], ["all", "HISTORY"]];

export default function AlertCenter({ onNavigate }) {
  const [alerts, setAlerts] = useState(null);
  const [filter, setFilter] = useState("new");

  const load = (f = filter) =>
    api(`/command/alerts?history=${f === "all" ? 1 : 0}`)
      .then((d) => setAlerts(d.alerts || []))
      .catch(() => setAlerts([]));

  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line

  const act = (id, action) =>
    api("/command/alerts/act", { method: "post", data: { id, action } })
      .then(() => load()).catch(() => {});

  const ackAll = () =>
    api("/command/alerts/ack-all", { method: "post", data: {} })
      .then(() => load()).catch(() => {});

  const hasNew = (alerts || []).some((a) => a.status === "new");

  return (
    <div className="px-4 py-3" data-testid="alert-center">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#D92D20]">
          // Oracle Alerts
        </span>
        <div className="ml-auto flex gap-1 items-center">
          {hasNew && (
            <button type="button" onClick={ackAll}
                    data-testid="alerts-ack-all"
                    className="font-mono text-[8px] uppercase tracking-[0.15em] px-2 py-0.5 border border-[#2D2A26] text-[#5A554C] hover:text-[#E4DFD3] hover:border-[#A39E93]">
              Acknowledge all
            </button>
          )}          {FILTERS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setFilter(id)}
                    data-testid={`alerts-filter-${id}`}
                    className={`font-mono text-[8px] uppercase tracking-[0.15em] px-2 py-0.5 border ${
                      filter === id ? "border-[#D92D20] text-[#E4DFD3]"
                        : "border-[#2D2A26] text-[#5A554C] hover:text-[#A39E93]"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {alerts === null && (
        <div className="font-mono text-[10px] text-[#5A554C] py-3 animate-pulse">Scanning signals…</div>
      )}
      {alerts?.length === 0 && (
        <div className="font-mono text-[10px] text-[#5A554C] py-3" data-testid="alerts-empty">
          {filter === "new" ? "No active alerts. All monitored signals nominal."
            : "No alert history on file."}
        </div>
      )}
      {(alerts || []).map((a) => {
        const sv = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.info;
        return (
          <div key={a.id} className="border-l-2 pl-3 py-2 mb-2"
               style={{ borderColor: sv.color }} data-testid={`alert-${a.id}`}>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[8px] uppercase tracking-[0.2em]" style={{ color: sv.color }}>
                {sv.label}
              </span>
              <span className="font-mono text-[8px] text-[#5A554C]">{a.created_at}</span>
              {a.status !== "new" && (
                <span className="font-mono text-[8px] uppercase text-[#5A554C]">· {a.status}</span>
              )}
            </div>
            <button type="button"
                    onClick={() => { if (a.url) { act(a.id, "read"); onNavigate(a.url); } }}
                    className="block text-left font-mono text-[12px] text-[#E4DFD3] hover:text-[#D92D20] transition-colors mt-0.5">
              {a.title}
            </button>
            <div className="font-mono text-[10px] text-[#A39E93] leading-relaxed">{a.reason}</div>
            {a.status === "new" && (
              <div className="flex gap-2 mt-1.5">
                {[["read", Eye, "Read"], ["complete", Check, "Complete"],
                  ["dismiss", X, "Dismiss"], ["archive", ArchiveIcon, "Archive"]].map(([id, Icon, label]) => (
                  <button key={id} type="button" onClick={() => act(a.id, id)}
                          data-testid={`alert-act-${id}-${a.id}`}
                          className="flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.15em] text-[#5A554C] hover:text-[#E4DFD3] transition-colors">
                    <Icon size={9} /> {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
